var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useContext, useState, useEffect } from 'react';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { findNumberFrameAtPosition, placeTokensInFrame, findNearestCell, findEmptyCells, } from '../helper/tokenDropHelper';
import { NF_CELL_SIZE } from '../constants/gridConstants';
import ToolTip from './ToolTip';
import { playAudio } from '../helper/audioHelper';
import { saveState } from '../helper/stateManagerHelper';
import { announce } from '../helper/announcer';
var TOKEN_TOOLTIP_MAP = {
    1: 'text.token1',
    5: 'text.token5',
    10: 'text.token10',
};
// Aria-label keys include color + shape so SR users hear "Add 1 Black Ladybug
// counter" instead of the generic "Add 1 Counter" tooltip text.
var TOKEN_ARIA_LABEL_MAP = {
    1: 'text.tokenAriaLabel1',
    5: 'text.tokenAriaLabel5',
    10: 'text.tokenAriaLabel10',
};
// Shape asset key → translation key for announcements ("shape0" → text.shape1 → "Butterfly")
var SHAPE_NAME_MAP = {
    shape0: 'text.shape1',
    shape1: 'text.shape2',
    shape2: 'text.shape3',
    shape3: 'text.shape4',
    shape4: 'text.shape5',
    shape5: 'text.shape6',
    shape6: 'text.shape7',
    shape7: 'text.shape8',
};
// Color asset folder name → translation key for announcements
var COLOR_NAME_MAP = {
    red: 'text.colorRed',
    yellow: 'text.colorYellow',
    blue: 'text.colorBlue',
    brown: 'text.colorBrown',
    orange: 'text.colorOrange',
    green: 'text.colorGreen',
    purple: 'text.colorPurple',
    black: 'text.colorBlack',
    white: 'text.colorWhite',
    gray: 'text.colorGray',
};
var DraggableToken = function (_a) {
    var _b, _c;
    var color = _a.color, shape = _a.shape, variant = _a.variant, panel = _a.panel, groupLabel = _a.groupLabel, _d = _a.tabbable, tabbable = _d === void 0 ? true : _d, rootRef = _a.rootRef, onActivateFocus = _a.onActivateFocus;
    var svgPath = "./assets/images/manipulative/numberFrames/".concat(color, "/").concat(shape, "/").concat(variant, ".svg");
    var _e = useContext(DataContext), panningEnable = _e.panningEnable, setManipulative = _e.setManipulative, manipulative = _e.manipulative, setSelectedTool = _e.setSelectedTool, selectedTool = _e.selectedTool, getPositionForNewItem = _e.getPositionForNewItem;
    var zoomLevel = globalStore.useStoreState(function (state) { return state.playerStore; }).zoomLevel;
    // Keep a ref so event-listener closures always read the latest value
    var manipulativeRef = useRef(manipulative);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    var zoomRef = useRef(zoomLevel);
    useEffect(function () {
        zoomRef.current = zoomLevel;
    }, [zoomLevel]);
    var selectedToolRef = useRef(selectedTool);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    // Refs for drag state
    var dragStartRef = useRef(null);
    var cloneRef = useRef(null);
    var hasMovedRef = useRef(false);
    // Flipped briefly after a drag-drop completes so the trailing browser `click`
    // event is skipped by `handleClick`. Needed because some screen readers (NVDA
    // + Chrome) synthesize a full mousedown → mouseup → click sequence when
    // activating a role="button" div, which would otherwise make a pointer-
    // timestamp guard indistinguishable from a real mouse click.
    var justDroppedRef = useRef(false);
    var handleMouseDown = function (e) {
        if (panningEnable) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        dragStartRef.current = { x: e.clientX, y: e.clientY };
        hasMovedRef.current = false;
        // Add window listeners
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };
    var handleTouchStart = function (e) {
        if (panningEnable)
            return;
        e.preventDefault();
        e.stopPropagation();
        var touch = e.touches[0];
        dragStartRef.current = { x: touch.clientX, y: touch.clientY };
        hasMovedRef.current = false;
        // Add window listeners — { passive: false } required so preventDefault() works in touchmove
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
    };
    var handleMouseMove = function (e) {
        if (!dragStartRef.current)
            return;
        var deltaX = e.clientX - dragStartRef.current.x;
        var deltaY = e.clientY - dragStartRef.current.y;
        var distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        // 5px movement threshold
        if (distance > 5 && !hasMovedRef.current) {
            hasMovedRef.current = true;
            createClone(e.clientX, e.clientY);
        }
        if (hasMovedRef.current && cloneRef.current) {
            // Update clone position
            cloneRef.current.style.left = "".concat(e.clientX, "px");
            cloneRef.current.style.top = "".concat(e.clientY, "px");
        }
    };
    var handleTouchMove = function (e) {
        if (!dragStartRef.current)
            return;
        e.preventDefault();
        var touch = e.touches[0];
        var deltaX = touch.clientX - dragStartRef.current.x;
        var deltaY = touch.clientY - dragStartRef.current.y;
        var distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (distance > 5 && !hasMovedRef.current) {
            hasMovedRef.current = true;
            createClone(touch.clientX, touch.clientY);
        }
        if (hasMovedRef.current && cloneRef.current) {
            cloneRef.current.style.left = "".concat(touch.clientX, "px");
            cloneRef.current.style.top = "".concat(touch.clientY, "px");
        }
    };
    var createClone = function (x, y) {
        // Create clone with just the image (no border)
        var clone = document.createElement('img');
        clone.src = svgPath;
        clone.style.position = 'fixed';
        clone.style.left = "".concat(x, "px");
        clone.style.top = "".concat(y, "px");
        clone.style.width = '50px'; // Fixed size for clone
        clone.style.height = '50px';
        clone.style.transform = 'translate(-50%, -50%) scale(1.1)';
        clone.style.pointerEvents = 'none';
        clone.style.zIndex = '10000';
        clone.style.opacity = '0.9';
        clone.style.transition = 'none';
        document.body.appendChild(clone);
        cloneRef.current = clone;
    };
    var cleanup = function () {
        // Remove clone
        if (cloneRef.current) {
            document.body.removeChild(cloneRef.current);
            cloneRef.current = null;
        }
        // Remove listeners
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        dragStartRef.current = null;
        hasMovedRef.current = false;
    };
    var handleDrop = function (clientX, clientY) {
        var _a, _b, _c, _d;
        // Get scroll offset from the canvas scroll container
        var scrollElement = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        var scrollLeft = (scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollLeft) || 0;
        var scrollTop = (scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollTop) || 0;
        // Get the container's position in the viewport
        var containerRect = scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.getBoundingClientRect();
        var containerLeft = (containerRect === null || containerRect === void 0 ? void 0 : containerRect.left) || 0;
        var containerTop = (containerRect === null || containerRect === void 0 ? void 0 : containerRect.top) || 0;
        // Abort if the drop point is outside the workspace viewport — e.g. the
        // user dragged back onto the right-side panel, sidebar, header, or
        // footer. Without this guard handleDrop would still translate the pointer
        // into canvas coords and place the counter off-screen.
        if (!containerRect ||
            clientX < containerRect.left ||
            clientX > containerRect.right ||
            clientY < containerRect.top ||
            clientY > containerRect.bottom) {
            return;
        }
        // The canvas spans the whole page; UI panels (right-side drawer, header,
        // footer, zoom/pan, sidebars) sit visually ON TOP of it. A bounds check
        // alone isn't enough — also reject drops that land inside any overlay.
        // Mirrors LinkingCubesToolbar.tsx's drop-guard pattern.
        var isInsideEl = function (el) {
            if (!el)
                return false;
            var r = el.getBoundingClientRect();
            return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
        };
        if (isInsideEl(document.querySelector('#rightSideDrawerContainer')) ||
            isInsideEl(document.querySelector('#rightSideToolPanel')) ||
            isInsideEl(document.querySelector('.headerContainer')) ||
            isInsideEl(document.querySelector('.zoomAndPanControlPanel')) ||
            isInsideEl(document.querySelector('.manipulativeTool')) ||
            isInsideEl(document.querySelector('.footer'))) {
            return;
        }
        var zoom = zoomRef.current;
        var canvasX = (clientX - containerLeft + scrollLeft) / zoom;
        var canvasY = (clientY - containerTop + scrollTop) / zoom;
        // Check if dropped on a numberFrame
        var targetFrame = findNumberFrameAtPosition(clientX - containerLeft, clientY - containerTop, manipulativeRef.current, zoom, { x: scrollLeft, y: scrollTop });
        if (targetFrame && !targetFrame.lock.editing) {
            // Drop on frame with overflow handling
            var nearestCell = findNearestCell(canvasX, canvasY, targetFrame);
            var _e = placeTokensInFrame(targetFrame, variant, color, shape, nearestCell), newCounters_1 = _e.newCounters, overflowCounters_1 = _e.overflowCounters, updatedTokensMap_1 = _e.updatedTokensMap;
            var placed = newCounters_1.length;
            playAudio(placed >= 9 ? 'nfDrop10' : placed >= 5 ? 'nfDrop5' : 'nfDrop1', { volume: 0.5 });
            if (overflowCounters_1.length > 0) {
                var overflow = overflowCounters_1.length;
                playAudio(overflow >= 9 ? 'canvasDrop10' : overflow >= 5 ? 'canvasDrop5' : 'canvasDrop1', {
                    volume: 0.5,
                });
            }
            // Atomic update: update frame's tokens map + create all counters
            var oldFrameTokens = __assign({}, (targetFrame.tokens || {}));
            setManipulative(function (prev) {
                var updated = prev.map(function (item) {
                    if (item.data.id === targetFrame.id && item.data.type === 'numberFrames') {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { tokens: updatedTokensMap_1 }) });
                    }
                    return item;
                });
                return __spreadArray(__spreadArray(__spreadArray([], updated, true), newCounters_1, true), overflowCounters_1, true);
            });
            // Save counter creations + frame tokens change as a single undoable batch
            var batchSave = __spreadArray(__spreadArray(__spreadArray([], newCounters_1.map(function (c) { return ({
                type: c.data.id,
                id: c.data.id,
                propertyName: 'create',
                old: JSON.stringify(c),
            }); }), true), overflowCounters_1.map(function (c) { return ({
                type: c.data.id,
                id: c.data.id,
                propertyName: 'create',
                old: JSON.stringify(c),
            }); }), true), [
                {
                    type: targetFrame.id,
                    id: targetFrame.id,
                    propertyName: 'tokens',
                    old: JSON.stringify(oldFrameTokens),
                    new: JSON.stringify(updatedTokensMap_1),
                },
            ], false);
            saveState(batchSave);
            // Select all placed counters (in-frame + overflow)
            var allPlaced = __spreadArray(__spreadArray([], newCounters_1, true), overflowCounters_1, true).map(function (c) { return c.data.id; });
            if (allPlaced.length > 0) {
                setSelectedTool(allPlaced);
            }
            // Announce outcome — frame-placed vs overflow are separate announcements
            // so AT users hear both when a partial drop happens (some fit, some spill).
            var colorLabel = t((_a = COLOR_NAME_MAP[color]) !== null && _a !== void 0 ? _a : '');
            var shapeLabel = t((_b = SHAPE_NAME_MAP[shape]) !== null && _b !== void 0 ? _b : '');
            var placedInFrame = newCounters_1.length;
            if (placedInFrame > 0) {
                var firstCellEntry = Object.entries(updatedTokensMap_1).find(function (_a) {
                    var v = _a[1];
                    return newCounters_1.some(function (c) { return c.data.id === v; });
                });
                var cellIdx = firstCellEntry ? parseInt(firstCellEntry[0], 10) : 0;
                var row = Math.floor(cellIdx / targetFrame.cols) + 1;
                var col = (cellIdx % targetFrame.cols) + 1;
                announce(t(placedInFrame === 1 ? 'text.tokenPlacedSingle' : 'text.tokenPlacedPlural', {
                    count: placedInFrame,
                    color: colorLabel,
                    shape: shapeLabel,
                    row: row,
                    col: col,
                }));
            }
            if (overflowCounters_1.length > 0) {
                announce(t(overflowCounters_1.length === 1
                    ? 'text.tokenPlacedOutsideSingle'
                    : 'text.tokenPlacedOutsidePlural', { count: overflowCounters_1.length, color: colorLabel, shape: shapeLabel }));
            }
        }
        else {
            // Dropped outside all frames — start at drop position, wrap counters that touch a frame
            var allFrames_1 = manipulativeRef.current
                .filter(function (m) { return m.data.type === 'numberFrames'; })
                .map(function (m) { return m.data; });
            // Helper: does canvas point (cx, cy) overlap any frame (using counter radius as margin)
            var counterRadius_1 = 25; // half of 50px default counter size
            var overlapsFrame = function (cx, cy) {
                var _a;
                for (var _i = 0, allFrames_2 = allFrames_1; _i < allFrames_2.length; _i++) {
                    var frame = allFrames_2[_i];
                    var fs = (_a = frame.scale) !== null && _a !== void 0 ? _a : 1;
                    var halfW = ((frame.cols * NF_CELL_SIZE) / 2) * fs + counterRadius_1;
                    var halfH = ((frame.rows * NF_CELL_SIZE) / 2) * fs + counterRadius_1;
                    var lx = cx - frame.center.x;
                    var ly = cy - frame.center.y;
                    if (frame.rotation) {
                        var a = -(frame.rotation * Math.PI) / 180;
                        var rx = lx * Math.cos(a) - ly * Math.sin(a);
                        var ry = lx * Math.sin(a) + ly * Math.cos(a);
                        lx = rx;
                        ly = ry;
                    }
                    if (Math.abs(lx) <= halfW && Math.abs(ly) <= halfH)
                        return frame;
                }
                return null;
            };
            // Line-break algorithm: spread right from drop point; wrap to next row when hitting a frame
            var newCounters_2 = [];
            var curX = canvasX;
            var curY = canvasY;
            for (var i = 0; i < variant; i++) {
                // If current position hits a frame, wrap to next row and retry
                while (overlapsFrame(curX, curY)) {
                    curY += NF_CELL_SIZE;
                    curX = canvasX;
                }
                var cx = curX;
                var cy = curY;
                curX += NF_CELL_SIZE;
                var tokenId = "numberFrameCounter_".concat(Date.now(), "_").concat(Math.random()
                    .toString(36)
                    .substr(2, 9));
                var baseTokenSize = shape === 'shape2' ? 55 : 50;
                newCounters_2.push({
                    type: 'numberFrameCounter',
                    data: {
                        id: tokenId,
                        type: 'numberFrameCounter',
                        shape: shape,
                        color: color,
                        center: { x: cx, y: cy },
                        dimensions: { width: baseTokenSize, height: baseTokenSize },
                        scale: 1,
                        rotation: 0,
                        lock: { positioning: false, editing: false },
                        defaultDimensions: { width: baseTokenSize, height: baseTokenSize },
                    },
                });
            }
            playAudio(variant === 10 ? 'canvasDrop10' : variant === 5 ? 'canvasDrop5' : 'canvasDrop1', {
                volume: 0.5,
            });
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), newCounters_2, true); });
            saveState(newCounters_2.map(function (c) { return ({
                type: c.data.id,
                id: c.data.id,
                propertyName: 'create',
                old: JSON.stringify(c),
            }); }));
            setSelectedTool(newCounters_2.map(function (c) { return c.data.id; }));
            // Dropped in empty workspace area — counters are not attached to any frame.
            var colorLabel = t((_c = COLOR_NAME_MAP[color]) !== null && _c !== void 0 ? _c : '');
            var shapeLabel = t((_d = SHAPE_NAME_MAP[shape]) !== null && _d !== void 0 ? _d : '');
            announce(t(newCounters_2.length === 1
                ? 'text.tokenPlacedOutsideSingle'
                : 'text.tokenPlacedOutsidePlural', { count: newCounters_2.length, color: colorLabel, shape: shapeLabel }));
        }
    };
    var handleMouseUp = function (e) {
        if (hasMovedRef.current) {
            handleDrop(e.clientX, e.clientY);
            // Skip the trailing browser-synthesized click so we don't double-place.
            justDroppedRef.current = true;
            setTimeout(function () {
                justDroppedRef.current = false;
            }, 200);
        }
        cleanup();
    };
    var handleTouchEnd = function (e) {
        if (hasMovedRef.current && e.changedTouches.length > 0) {
            var touch = e.changedTouches[0];
            handleDrop(touch.clientX, touch.clientY);
            justDroppedRef.current = true;
            setTimeout(function () {
                justDroppedRef.current = false;
            }, 200);
        }
        cleanup();
    };
    var _f = useState(false), hovered = _f[0], setHovered = _f[1];
    // Keyboard activation: place `variant` tokens on the first real frame, starting
    // at its first empty cell. Mirrors the frame-target branch of handleDrop.
    // `skipSelect` is set for keyboard + VoiceOver synthetic-click activations so
    // the newly-placed counters aren't auto-selected. Mouse drag-drop still
    // auto-selects (its own handleDrop path calls setSelectedTool directly).
    var placeOnFirstEmptyCell = function (skipSelect) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (skipSelect === void 0) { skipSelect = false; }
        if (panningEnable)
            return;
        // Prefer the currently-selected frame (or a selected counter's parent
        // frame) so keyboard/SR users dropping tokens after selecting — or
        // duplicating — a specific frame target that frame, not whichever frame
        // happens to be first in the manipulative array.
        var allFrames = manipulativeRef.current.filter(function (m) { return m.data.type === 'numberFrames' && !String(m.data.id).endsWith('_dummyForRightTool'); });
        var targetFrame;
        var _loop_1 = function (sid) {
            var frame = allFrames.find(function (m) { return m.data.id === sid; });
            if (frame) {
                targetFrame = frame.data;
                return "break";
            }
            var item = manipulativeRef.current.find(function (m) { return m.data.id === sid; });
            if ((item === null || item === void 0 ? void 0 : item.data.type) === 'numberFrameCounter') {
                var parentId_1 = item.data.frameId;
                var parent_1 = parentId_1 ? allFrames.find(function (m) { return m.data.id === parentId_1; }) : undefined;
                if (parent_1) {
                    targetFrame = parent_1.data;
                    return "break";
                }
            }
        };
        for (var _i = 0, _j = selectedToolRef.current; _i < _j.length; _i++) {
            var sid = _j[_i];
            var state_1 = _loop_1(sid);
            if (state_1 === "break")
                break;
        }
        // Fallback for the edit-locked / full-frame cases: once a click places
        // tokens outside a frame, those counters have no `frameId`, so the
        // selection-based lookup above can't find their "home" frame. Without
        // this, subsequent clicks would slide to allFrames[0] — i.e., a
        // different frame than the one the user is working with. Resolve by
        // proximity: whichever frame is closest to the currently-selected
        // counter is still the user's target frame.
        if (!targetFrame && allFrames.length > 0) {
            var _loop_2 = function (sid) {
                var item = manipulativeRef.current.find(function (m) { return m.data.id === sid; });
                if ((item === null || item === void 0 ? void 0 : item.data.type) !== 'numberFrameCounter')
                    return "continue";
                var counterCenter = item.data.center;
                if (!counterCenter)
                    return "continue";
                var nearest = void 0;
                var nearestDistSq = Infinity;
                for (var _o = 0, allFrames_3 = allFrames; _o < allFrames_3.length; _o++) {
                    var fItem = allFrames_3[_o];
                    var f = fItem.data;
                    var dx = f.center.x - counterCenter.x;
                    var dy = f.center.y - counterCenter.y;
                    var distSq = dx * dx + dy * dy;
                    if (distSq < nearestDistSq) {
                        nearest = f;
                        nearestDistSq = distSq;
                    }
                }
                if (nearest) {
                    targetFrame = nearest;
                    return "break";
                }
            };
            for (var _k = 0, _l = selectedToolRef.current; _k < _l.length; _k++) {
                var sid = _l[_k];
                var state_2 = _loop_2(sid);
                if (state_2 === "break")
                    break;
            }
        }
        // No frame in scope (nothing selected, or selected items don't resolve to
        // a frame) → place standalone counters on the workspace. Mirrors the
        // mouse-drag "drop on empty workspace" branch in handleDrop. Skipping
        // the previous `allFrames[0]` auto-fallback intentionally — auto-using
        // some random frame the user never asked for was the original bug.
        if (!targetFrame) {
            var baseTokenSize = shape === 'shape2' ? 55 : 50;
            var anchor = getPositionForNewItem();
            // Collision avoidance against every existing counter on the canvas
            // so repeated keyboard activations don't stack on the same spot.
            var occupied_1 = manipulativeRef.current
                .filter(function (m) { return m.data.type === 'numberFrameCounter'; })
                .map(function (m) { return ({
                x: m.data.center.x,
                y: m.data.center.y,
            }); });
            var minDistSq_1 = baseTokenSize * baseTokenSize;
            var collides = function (x, y) {
                return occupied_1.some(function (c) {
                    var dx = c.x - x;
                    var dy = c.y - y;
                    return dx * dx + dy * dy < minDistSq_1;
                });
            };
            // Spread tokens horizontally from the anchor, wrapping naturally.
            var spacing = baseTokenSize + 6;
            var standaloneCounters_1 = [];
            for (var i = 0; i < variant; i++) {
                var x = anchor.x + i * spacing;
                var y = anchor.y;
                // If this slot collides, jitter outward in a small spiral.
                for (var tries = 0; tries < 12 && collides(x, y); tries++) {
                    x += spacing;
                    if (tries > 0 && tries % 4 === 0) {
                        x = anchor.x;
                        y += spacing;
                    }
                }
                occupied_1.push({ x: x, y: y });
                var tokenId = "numberFrameCounter_".concat(Date.now(), "_").concat(i, "_").concat(Math.random()
                    .toString(36)
                    .substr(2, 9));
                standaloneCounters_1.push({
                    type: 'numberFrameCounter',
                    data: {
                        id: tokenId,
                        type: 'numberFrameCounter',
                        shape: shape,
                        color: color,
                        center: { x: x, y: y },
                        dimensions: { width: baseTokenSize, height: baseTokenSize },
                        scale: 1,
                        rotation: 0,
                        lock: { positioning: false, editing: false },
                        defaultDimensions: { width: baseTokenSize, height: baseTokenSize },
                    },
                });
            }
            playAudio(variant === 10 ? 'canvasDrop10' : variant === 5 ? 'canvasDrop5' : 'canvasDrop1', {
                volume: 0.5,
            });
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), standaloneCounters_1, true); });
            saveState(standaloneCounters_1.map(function (c) { return ({
                type: c.data.id,
                id: c.data.id,
                propertyName: 'create',
                old: JSON.stringify(c),
            }); }));
            var placedIds = standaloneCounters_1.map(function (c) { return c.data.id; });
            if (placedIds.length > 0 && !skipSelect)
                setSelectedTool(placedIds);
            var colorLabel_1 = t((_a = COLOR_NAME_MAP[color]) !== null && _a !== void 0 ? _a : '');
            var shapeLabel_1 = t((_b = SHAPE_NAME_MAP[shape]) !== null && _b !== void 0 ? _b : '');
            announce(t(variant === 1 ? 'text.tokenPlacedOutsideSingle' : 'text.tokenPlacedOutsidePlural', {
                count: variant,
                color: colorLabel_1,
                shape: shapeLabel_1,
            }));
            return;
        }
        var emptyCells = findEmptyCells(targetFrame);
        var frameIsFull = emptyCells.length === 0;
        // Place tokens outside the frame when either the frame is edit-locked
        // (mirrors the drag-and-drop rule in handleDrop) or every cell is already
        // occupied. Each token lands at a random position along one of the four
        // borders, with collision avoidance so repeated clicks don't stack on the
        // same spot.
        if (((_c = targetFrame.lock) === null || _c === void 0 ? void 0 : _c.editing) || frameIsFull) {
            var frameScale = (_d = targetFrame.scale) !== null && _d !== void 0 ? _d : 1;
            var frameWidth_1 = targetFrame.cols * NF_CELL_SIZE * frameScale;
            var frameHeight_1 = targetFrame.rows * NF_CELL_SIZE * frameScale;
            var halfW_1 = frameWidth_1 / 2;
            var halfH_1 = frameHeight_1 / 2;
            var baseTokenSize_1 = shape === 'shape2' ? 55 : 50;
            // Pad so the token's visual shape clears the frame border.
            var gap_1 = 30 + baseTokenSize_1 / 2;
            // Seed the collision set with every existing counter center so new
            // placements avoid both canvas counters and tokens added earlier in
            // this batch.
            var occupied_2 = manipulativeRef.current
                .filter(function (m) { return m.data.type === 'numberFrameCounter'; })
                .map(function (m) { return ({
                x: m.data.center.x,
                y: m.data.center.y,
            }); });
            var minDistSq_2 = baseTokenSize_1 * baseTokenSize_1;
            var collides = function (x, y) {
                return occupied_2.some(function (c) {
                    var dx = c.x - x;
                    var dy = c.y - y;
                    return dx * dx + dy * dy < minDistSq_2;
                });
            };
            var pickOutsidePos = function () {
                // 0 = top, 1 = right, 2 = bottom, 3 = left. A small random depth
                // offset lets tokens stagger away from the border instead of all
                // lining up at the same distance.
                var side = Math.floor(Math.random() * 4);
                var depth = Math.random() * baseTokenSize_1;
                var x = targetFrame.center.x;
                var y = targetFrame.center.y;
                switch (side) {
                    case 0:
                        x += (Math.random() - 0.5) * frameWidth_1;
                        y -= halfH_1 + gap_1 + depth;
                        break;
                    case 1:
                        x += halfW_1 + gap_1 + depth;
                        y += (Math.random() - 0.5) * frameHeight_1;
                        break;
                    case 2:
                        x += (Math.random() - 0.5) * frameWidth_1;
                        y += halfH_1 + gap_1 + depth;
                        break;
                    case 3:
                        x -= halfW_1 + gap_1 + depth;
                        y += (Math.random() - 0.5) * frameHeight_1;
                        break;
                }
                return { x: x, y: y };
            };
            var outsideCounters_1 = [];
            for (var i = 0; i < variant; i++) {
                var pos = pickOutsidePos();
                // Retry a bounded number of times to find a non-overlapping spot.
                // Accept the last candidate on give-up so we never silently drop
                // a token.
                for (var tries = 0; tries < 20 && collides(pos.x, pos.y); tries++) {
                    pos = pickOutsidePos();
                }
                occupied_2.push(pos);
                var tokenId = "numberFrameCounter_".concat(Date.now(), "_").concat(i, "_").concat(Math.random()
                    .toString(36)
                    .substr(2, 9));
                outsideCounters_1.push({
                    type: 'numberFrameCounter',
                    data: {
                        id: tokenId,
                        type: 'numberFrameCounter',
                        shape: shape,
                        color: color,
                        center: { x: pos.x, y: pos.y },
                        dimensions: { width: baseTokenSize_1, height: baseTokenSize_1 },
                        scale: 1,
                        rotation: 0,
                        lock: { positioning: false, editing: false },
                        defaultDimensions: { width: baseTokenSize_1, height: baseTokenSize_1 },
                    },
                });
            }
            playAudio(variant === 10 ? 'canvasDrop10' : variant === 5 ? 'canvasDrop5' : 'canvasDrop1', {
                volume: 0.5,
            });
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), outsideCounters_1, true); });
            saveState(outsideCounters_1.map(function (c) { return ({
                type: c.data.id,
                id: c.data.id,
                propertyName: 'create',
                old: JSON.stringify(c),
            }); }));
            var placedIds = outsideCounters_1.map(function (c) { return c.data.id; });
            if (placedIds.length > 0 && !skipSelect)
                setSelectedTool(placedIds);
            var colorLabel_2 = t((_e = COLOR_NAME_MAP[color]) !== null && _e !== void 0 ? _e : '');
            var shapeLabel_2 = t((_f = SHAPE_NAME_MAP[shape]) !== null && _f !== void 0 ? _f : '');
            announce(t(variant === 1 ? 'text.tokenPlacedOutsideSingle' : 'text.tokenPlacedOutsidePlural', {
                count: variant,
                color: colorLabel_2,
                shape: shapeLabel_2,
            }));
            return;
        }
        // Frame has at least one empty cell — fill from the first empty cell.
        // (The full-frame and edit-locked cases bail out via the border-placement
        // branch above.)
        var startCellIndex = emptyCells[0];
        var _m = placeTokensInFrame(targetFrame, variant, color, shape, startCellIndex), newCounters = _m.newCounters, overflowCounters = _m.overflowCounters, updatedTokensMap = _m.updatedTokensMap;
        var placedInFrame = newCounters.length;
        if (placedInFrame > 0) {
            playAudio(placedInFrame >= 9 ? 'nfDrop10' : placedInFrame >= 5 ? 'nfDrop5' : 'nfDrop1', {
                volume: 0.5,
            });
        }
        if (overflowCounters.length > 0) {
            var overflow = overflowCounters.length;
            playAudio(overflow >= 9 ? 'canvasDrop10' : overflow >= 5 ? 'canvasDrop5' : 'canvasDrop1', {
                volume: 0.5,
            });
        }
        var oldFrameTokens = __assign({}, (targetFrame.tokens || {}));
        setManipulative(function (prev) {
            var updated = prev.map(function (item) {
                if (item.data.id === targetFrame.id && item.data.type === 'numberFrames') {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { tokens: updatedTokensMap }) });
                }
                return item;
            });
            return __spreadArray(__spreadArray(__spreadArray([], updated, true), newCounters, true), overflowCounters, true);
        });
        var batchSave = __spreadArray(__spreadArray([], newCounters.map(function (c) { return ({
            type: c.data.id,
            id: c.data.id,
            propertyName: 'create',
            old: JSON.stringify(c),
        }); }), true), overflowCounters.map(function (c) { return ({
            type: c.data.id,
            id: c.data.id,
            propertyName: 'create',
            old: JSON.stringify(c),
        }); }), true);
        // Skip the tokens-map entry when the frame was full — the map didn't change.
        if (placedInFrame > 0) {
            batchSave.push({
                type: targetFrame.id,
                id: targetFrame.id,
                propertyName: 'tokens',
                old: JSON.stringify(oldFrameTokens),
                new: JSON.stringify(updatedTokensMap),
            });
        }
        saveState(batchSave);
        var allPlaced = __spreadArray(__spreadArray([], newCounters, true), overflowCounters, true).map(function (c) { return c.data.id; });
        if (allPlaced.length > 0 && !skipSelect)
            setSelectedTool(allPlaced);
        var colorLabel = t((_g = COLOR_NAME_MAP[color]) !== null && _g !== void 0 ? _g : '');
        var shapeLabel = t((_h = SHAPE_NAME_MAP[shape]) !== null && _h !== void 0 ? _h : '');
        if (placedInFrame > 0) {
            var row = Math.floor(startCellIndex / targetFrame.cols) + 1;
            var col = (startCellIndex % targetFrame.cols) + 1;
            announce(t(variant === 1 ? 'text.tokenPlacedSingle' : 'text.tokenPlacedPlural', {
                count: variant,
                color: colorLabel,
                shape: shapeLabel,
                row: row,
                col: col,
            }));
        }
        else {
            // Frame was full — all tokens were placed outside.
            announce(t(variant === 1 ? 'text.tokenPlacedOutsideSingle' : 'text.tokenPlacedOutsidePlural', {
                count: variant,
                color: colorLabel,
                shape: shapeLabel,
            }));
        }
    };
    var handleKeyDown = function (e) {
        if (e.key !== 'Enter' && e.key !== ' ')
            return;
        e.preventDefault();
        e.stopPropagation();
        placeOnFirstEmptyCell(true); // keyboard — don't auto-select
    };
    // Single activation path for keyboard, screen readers (NVDA/JAWS/VoiceOver),
    // and mouse clicks without drag. The only case we skip is the trailing
    // browser click that fires after a completed drag — `justDroppedRef` covers
    // that. A timestamp-based guard didn't work under NVDA+Chrome because that
    // stack synthesizes a full mousedown → mouseup → click sequence on role=button
    // activation, which is indistinguishable from a real mouse click by timing.
    var handleClick = function (e) {
        if (panningEnable)
            return;
        if (justDroppedRef.current)
            return; // drag path already placed
        e.preventDefault();
        e.stopPropagation();
        // detail === 0 = keyboard Enter/Space synthetic click OR VoiceOver / JAWS
        // double-tap / activation. In those paths skip auto-select so the newly
        // placed counters don't hijack focus from the AT user.
        placeOnFirstEmptyCell(e.detail === 0);
    };
    return (_jsxs("div", { ref: rootRef, className: "draggable-token", role: "button", tabIndex: tabbable ? 0 : -1, "aria-label": t(TOKEN_ARIA_LABEL_MAP[variant], {
            color: t((_b = COLOR_NAME_MAP[color]) !== null && _b !== void 0 ? _b : color),
            shape: t((_c = SHAPE_NAME_MAP[shape]) !== null && _c !== void 0 ? _c : shape),
        }), onMouseDown: handleMouseDown, onTouchStart: handleTouchStart, onClick: handleClick, onKeyDown: handleKeyDown, onMouseEnter: function () { return setHovered(true); }, onMouseLeave: function () { return setHovered(false); }, onFocus: function () {
            setHovered(true);
            onActivateFocus === null || onActivateFocus === void 0 ? void 0 : onActivateFocus();
        }, onBlur: function () { return setHovered(false); }, children: [_jsx("img", { src: svgPath, alt: "", draggable: "false" }), variant !== 1 && _jsx("div", { className: 'tokenNumber', children: variant }), _jsx(ToolTip, { buttonHovered: hovered, text: TOKEN_TOOLTIP_MAP[variant], direction: "left", isParentRelative: true })] }));
};
export default DraggableToken;
